import React, { useState, useMemo } from 'react';
import { Card } from '@components/atoms/Card';
import { TaskTableHeader } from '../TaskTableHeader';
import { TaskRow } from '@pages/schedule/components/TaskRow';
import { getTaskStatusDisplay, translateTaskDisplayValue } from '@utils/taskDisplay';
import { TaskStatus } from '@models/task.model';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

interface TaskData {
  id: string;
  name: string;
  startTime: string | Date;
  endTime: string | Date;
  budget?: number | string;
  status?: string;
  isEvent?: boolean;
  children?: TaskData[];
  delegateOrganization?: { name: string };
  originalOrganizationId?: string;
  superintendentId?: string;
}

interface TaskTableProps {
  tasks: TaskData[];
  taskBudgetRequests?: Map<string, any[]>;
  formatDate: (date: string | Date) => string;
  canApprove?: (task: TaskData) => boolean;
  loading?: boolean;
  onApprove?: (taskId: string) => void;
  onReject?: (taskId: string) => void;
  onEdit?: (task: TaskData) => void;
  onTaskClick?: (task: TaskData) => void;
  onDateClick?: (dateValue: string | Date) => void;
  className?: string;
}

export const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  taskBudgetRequests,
  formatDate,
  canApprove,
  onApprove,
  onReject,
  onEdit,
  onTaskClick,
  onDateClick,
  className = '',
}) => {
  const [approvalFilter, setApprovalFilter] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [loadingTasks, setLoadingTasks] = useState<Set<string>>(new Set());
  // Sprint 28 change request (global sweep, defect fix). `statusDisplay.label` (passed to
  // `TaskRow`) used to be raw English text — see `@utils/taskDisplay.ts`'s
  // `TaskDisplayInfo.label` comment.
  const tTaskDetail = useTranslation('taskDetail');

  // Sort tasks by start time
  const sortedTasks = useMemo(() => {
    return [...tasks].sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );
  }, [tasks]);

  // Filter tasks based on approval filter
  const filteredTasks = useMemo(() => {
    if (!approvalFilter) return sortedTasks;

    const filterTasksRecursively = (task: TaskData): TaskData | null => {
      const filteredChildren = task.children
        ? (task.children
            .map(filterTasksRecursively)
            .filter(Boolean) as TaskData[])
        : [];

      if (
        task.status === TaskStatus.CLIENT_APPROVAL ||
        task.status === TaskStatus.INTERNAL_APPROVAL ||
        filteredChildren.length > 0
      ) {
        return { ...task, children: filteredChildren };
      }

      return null;
    };

    return sortedTasks
      .map(filterTasksRecursively)
      .filter(Boolean) as TaskData[];
  }, [approvalFilter, sortedTasks]);

  const pendingTasksCount = useMemo(() => {
    return tasks.filter(
      (task) =>
        task.status === TaskStatus.CLIENT_APPROVAL ||
        task.status === TaskStatus.INTERNAL_APPROVAL,
    ).length;
  }, [tasks]);

  const handleApprove = async (taskId: string) => {
    setLoadingTasks((prev) => new Set(prev).add(taskId));
    try {
      await onApprove?.(taskId);
    } finally {
      setLoadingTasks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleReject = async (taskId: string) => {
    setLoadingTasks((prev) => new Set(prev).add(taskId));
    try {
      await onReject?.(taskId);
    } finally {
      setLoadingTasks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleEdit = (task: TaskData) => {
    onEdit?.(task);
  };

  const toggleExpanded = (taskId: string) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const renderTaskRow = (task: TaskData, isSubtask = false) => {
    const rawStatusDisplay = getTaskStatusDisplay(
      task,
      taskBudgetRequests?.get(task.id) || [],
    );
    const statusDisplay = {
      ...rawStatusDisplay,
      label: translateTaskDisplayValue(rawStatusDisplay.displayValue, tTaskDetail),
    };
    const isExpanded = expandedTasks.has(task.id);
    const isLoading = loadingTasks.has(task.id);
    const showApproval = canApprove?.(task) || false;

    return (
      <React.Fragment key={task.id}>
        <TaskRow
          task={task}
          statusDisplay={statusDisplay}
          formatDate={formatDate}
          showApprovalActions={showApproval}
          showChildren={isExpanded}
          loading={isLoading}
          onApprove={() => handleApprove(task.id)}
          onReject={() => handleReject(task.id)}
          onEdit={() => handleEdit(task)}
          onToggleExpand={() => {
            toggleExpanded(task.id);
          }}
          onTaskClick={() => onTaskClick?.(task)}
          onDateClick={onDateClick}
          className={isSubtask ? 'bg-gray-25 dark:bg-gray-850' : ''}>
          {task.children && isExpanded && (
            <div className="space-y-1">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-600">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"></th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      📋 SubTask
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      Start
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      End
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      Budget
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      Awaiting Approval
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"></th>
                  </tr>
                </thead>
                <tbody>
                  {task.children.map((child) => renderTaskRow(child, true))}
                </tbody>
              </table>
            </div>
          )}
        </TaskRow>
      </React.Fragment>
    );
  };

  return (
    <Card
      className={cn(
        'shadow-lg rounded-xl border border-gray-200 dark:border-gray-600 p-0',
        className,
      )}>
      <TaskTableHeader
        totalTasks={tasks.length}
        pendingTasks={pendingTasksCount}
        showApprovalFilter={true}
        approvalFilterChecked={approvalFilter}
        onApprovalFilterChange={setApprovalFilter}
      />

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 w-12"></th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Task
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Start
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                End
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Budget
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Actions
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300"></th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
            {filteredTasks.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 text-sm">
                  No tasks found
                </td>
              </tr>
            ) : (
              filteredTasks.map((task) => renderTaskRow(task))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
